import combinedQuery from 'graphql-combine-query'
import { get } from 'lodash'
import { Query_Root } from 'src/@types/graphql'
import {
  fetchFactsheetClassificationCategoriesAndScores,
  fetchFactsheetClassificationScoreValuesByFactsheetId,
  fetchFactsheetEffectivenessClassificationReason,
} from 'src/screens/factsheets/factsheet/factsheetQueries'
import { Option } from 'src/shared/form/control'
import { Client } from 'urql'

export type ClassificationCategoryAndScores = {
  categoryId: number
  categoryName: any
  scores: Option[]
}

export const CATEGORY_ID_PREFIX = 'category_'

export type ClassificationScoresForm = {
  classificationReason?: string
  // key is string category_categoryId, value is scoreId
  scores: Record<string, number | undefined>
}

export type FactsheetEffectivenessClassificationData = {
  factsheetClassificationCategories: ClassificationCategoryAndScores[]
  values: ClassificationScoresForm
}

const mapFactsheetClassificationCategories = (
  categories: Query_Root['factsheet_effectiveness_classification_category'],
  locale: string,
): ClassificationCategoryAndScores[] =>
  categories.map((category) => {
    const scores = category.factsheet_effectiveness_classification_scores.map((score) => {
      const scoreOption: Option = {
        label: get(score.name, locale),
        value: score.id,
      }
      return scoreOption
    })

    return {
      categoryId: category.id,
      categoryName: get(category.name, locale),
      scores,
    }
  })

export const fetchFactsheetClassificationCategories = async (
  urqlClient: Client,
  locale: string,
): Promise<ClassificationCategoryAndScores[]> => {
  const { data, error } = await urqlClient
    .query<{
      factsheet_effectiveness_classification_category: Query_Root['factsheet_effectiveness_classification_category']
    }>(fetchFactsheetClassificationCategoriesAndScores, undefined)
    .toPromise()

  if (error) {
    throw error
  }

  return mapFactsheetClassificationCategories(data?.factsheet_effectiveness_classification_category ?? [], locale)
}

export const fetchFactsheetEffectivenessClassificationData = async (
  factsheetId: number,
  urqlClient: Client,
  locale: string,
) => {
  const combinedQueryResult = combinedQuery('FactsheetEffectivenessClassificationData')
    .add<{
      factsheet_effectiveness_classification_category: Query_Root['factsheet_effectiveness_classification_category']
    }>(fetchFactsheetClassificationCategoriesAndScores)
    .add<
      {
        factsheet_effectiveness_classification_score_value: Query_Root['factsheet_effectiveness_classification_score_value']
      },
      { factsheetId: number }
    >(fetchFactsheetClassificationScoreValuesByFactsheetId, { factsheetId: factsheetId })
    .add<{ factsheet_by_pk: Query_Root['factsheet_by_pk'] }, { id: number }>(
      fetchFactsheetEffectivenessClassificationReason,
      { id: factsheetId },
    )

  const { document, variables } = combinedQueryResult

  const { data, error } = await urqlClient
    .query<{
      factsheet_effectiveness_classification_category: Query_Root['factsheet_effectiveness_classification_category']
      factsheet_effectiveness_classification_score_value: Query_Root['factsheet_effectiveness_classification_score_value']
      factsheet_by_pk: Query_Root['factsheet_by_pk']
    }>(document, variables)
    .toPromise()

  if (error) {
    throw error
  }

  const transformedData = mapFactsheetClassificationCategories(
    data?.factsheet_effectiveness_classification_category ?? [],
    locale,
  )

  const values: ClassificationScoresForm = {
    scores: {},
    classificationReason: data?.factsheet_by_pk?.effectiveness_classification_reason ?? '',
  }

  data?.factsheet_effectiveness_classification_score_value.forEach((score) => {
    values.scores[`${CATEGORY_ID_PREFIX}${score.category_id}`] = score.score_id
  })

  const factsheetEffectivenessClassificationData: FactsheetEffectivenessClassificationData = {
    factsheetClassificationCategories: transformedData,
    values: values,
  }

  return factsheetEffectivenessClassificationData
}
